import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";

import { Product } from "../../../../services/productService";

export interface ConsumptionLine {
  key: string;
  product_id: number;
  product_name: string;
  quantity_consumed: number;
}

interface CheckoutConsumptionModalProps {
  isOpen: boolean;
  toggle: () => void;
  orderTotal: number;
  formatMoney: (cents: number) => string;
  internalProducts: Product[];
  loadingProducts: boolean;
  consumptionLines: ConsumptionLine[];
  draftProductId: string;
  draftQuantity: number;
  draftError: string | null;
  submitting: boolean;
  onDraftProductChange: (value: string) => void;
  onDraftQuantityChange: (value: number) => void;
  onAddLine: () => void;
  onRemoveLine: (key: string) => void;
  onSkip: () => void;
  onConfirm: () => void;
}

const CheckoutConsumptionModal = ({
  isOpen,
  toggle,
  orderTotal,
  formatMoney,
  internalProducts,
  loadingProducts,
  consumptionLines,
  draftProductId,
  draftQuantity,
  draftError,
  submitting,
  onDraftProductChange,
  onDraftQuantityChange,
  onAddLine,
  onRemoveLine,
  onSkip,
  onConfirm,
}: CheckoutConsumptionModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" scrollable>
      <ModalHeader toggle={toggle}>
        {t("studio.orders.checkout_prompt.title")}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">{t("studio.orders.checkout_prompt.description")}</p>
        <p className="mb-4">
          <span className="text-muted me-2">{t("studio.orders.total.label")}:</span>
          <span className="fw-semibold">{formatMoney(orderTotal)}</span>
        </p>

        {loadingProducts ? (
          <div className="text-center py-4">
            <Spinner color="primary">{t("studio.orders.loading")}</Spinner>
          </div>
        ) : (
          <>
            <h6 className="mb-3">{t("studio.orders.checkout_prompt.add_consumption")}</h6>
            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-7">
                <Label htmlFor="consumption-product">
                  {t("studio.orders.checkout_prompt.internal_product_label")}
                </Label>
                <Input
                  id="consumption-product"
                  type="select"
                  value={draftProductId}
                  onChange={(event) => onDraftProductChange(event.target.value)}
                  invalid={!!draftError}
                >
                  <option value="">
                    {t("studio.orders.checkout_prompt.internal_product_placeholder")}
                  </option>
                  {internalProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </Input>
                {draftError && <FormFeedback className="d-block">{draftError}</FormFeedback>}
              </div>
              <div className="col-md-3">
                <Label htmlFor="consumption-quantity">
                  {t("studio.orders.checkout_prompt.quantity_label")}
                </Label>
                <Input
                  id="consumption-quantity"
                  type="number"
                  min={1}
                  value={draftQuantity}
                  onChange={(event) => onDraftQuantityChange(Number(event.target.value))}
                  invalid={!!draftError}
                />
              </div>
              <div className="col-md-2">
                <Button color="primary" className="w-100" onClick={onAddLine} disabled={submitting}>
                  {t("studio.orders.checkout_prompt.add_line")}
                </Button>
              </div>
            </div>

            {consumptionLines.length === 0 ? (
              <p className="text-muted mb-0">{t("studio.orders.checkout_prompt.empty_list")}</p>
            ) : (
              <div className="table-responsive">
                <Table size="sm" className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t("studio.orders.checkout_prompt.table.product")}</th>
                      <th>{t("studio.orders.checkout_prompt.table.quantity")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {consumptionLines.map((line) => (
                      <tr key={line.key}>
                        <td>{line.product_name}</td>
                        <td>{line.quantity_consumed}</td>
                        <td className="text-end">
                          <Button
                            color="soft-danger"
                            size="sm"
                            disabled={submitting}
                            onClick={() => onRemoveLine(line.key)}
                          >
                            {t("studio.orders.checkout_prompt.remove_line")}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={submitting}>
          {t("studio.orders.actions.cancel")}
        </Button>
        <Button color="secondary" onClick={onSkip} disabled={submitting || loadingProducts}>
          {t("studio.orders.checkout_prompt.skip_button")}
        </Button>
        <Button color="success" onClick={onConfirm} disabled={submitting || loadingProducts}>
          {submitting
            ? t("studio.orders.loading")
            : t("studio.orders.checkout_prompt.confirm_button")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CheckoutConsumptionModal;
